import { IncludeExcludeToggle } from "@/components/IncludeExcludeToggle";
import { RangeFilter } from "@/components/molecules/Filters/RangeFilter";
import { ScrollArea } from "@/components/ui/scroll-area";
import { SheetHeader } from "@/design-system/BottomSheet/SheetHeader";
import { Button } from "@/design-system/Button/Button";
import { CheckBox } from "@/design-system/Check/Check";
import { SectionLabel } from "@/design-system/SectionLabel/SectionLabel";
import { Switch } from "@/design-system/Switch/Switch";
import { Well } from "@/design-system/Well/Well";
import { cn } from "@/lib/utils";
import { showModal } from "@/redux/message/messageSlice";
import { SearchedPlayer } from "@/redux/players/SearchedPlayer.type";
import {
  SolveSettings,
  applySolveSettings,
  pickSolveSettings,
  sbcFiltersSelector,
} from "@/redux/sbcs/sbcFiltersSlice";
import { AppDispatch } from "@/redux/store";
import { getUserSelector } from "@/redux/user/userSlice";
import { Trans, useTranslation } from "@repo/shared/i18n";
import {
  INCLUDE_EXCLUDE_KEYS,
  type LaneKind,
  PRIORITISE_KEYS,
  RATING_MAX,
  RATING_MIN,
  type SolveSettingsBoolKey,
  addToLane,
  normaliseRatings,
  removeFromLane,
} from "@repo/shared/utils";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { PlayerSearch } from "./PlayerSearch";
import { SettingRow } from "./SettingRow";

/* The ordering and membership are shared; only the copy is web's. */
const LABELS = {
  useUntradeablesOnly: "customize.untradeablesOnly",
  prioritizeSbcStorage: "customize.prioritizeSbcStorage",
  prioritizeDuplicates: "customize.prioritizeDuplicates",
  includeTransferlist: "customize.prioritizeTransferList",
  excludeSbcPlayers: "customize.sbcPlayers",
  excludeObjectivePlayers: "customize.objectivePlayers",
  excludeActiveSquad: "customize.activeSquad",
} as const satisfies Record<SolveSettingsBoolKey, string>;

/* The Streamlined SBC's checkbox, as a full-width settings row. */
const CheckRow = ({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: () => void;
}) => (
  <label className="flex flex-row justify-between h-12 cursor-pointer select-none items-center px-3">
    <input
      type="checkbox"
      checked={checked}
      onChange={onChange}
      className="sr-only"
    />
    <span className="text-sm font-semibold text-neutral-100">{label}</span>
    <CheckBox
      checked={checked}
      className={cn(!checked && "border-neutral-600")}
    />
  </label>
);

/**
 * The advanced solve settings: rating range, the toggles, and the two player
 * lanes. Edits a draft and commits it on Apply; closing without applying
 * discards. The backend runs every pool, so there is no scope to pick here.
 */
export const SolveSettingsPanel = ({
  compact = false,
  mode,
  onApply,
  onDismiss,
  onClose,
  applyLabel,
}: {
  /** Phones: one column, larger rows, full-width Apply. */
  compact?: boolean;
  /**
   * Live: every change lands as made, and Apply only closes. Draft: changes
   * wait for Apply. Either way Apply is there, as the "ok, let's go".
   */
  mode: "live" | "draft";
  /** Called after Apply, so the surface can close. */
  onApply?: () => void;
  /** Called when a dialog takes over (club import), so the surface gets
      out of its way. */
  onDismiss?: () => void;
  /** Compact: the sheet header's minimize button. */
  onClose?: () => void;
  /** The primary button's label; the solution page says Solve Again. */
  applyLabel?: string;
}) => {
  const { t } = useTranslation(["sbcs", "common"]);
  const dispatch = useDispatch<AppDispatch>();
  const filters = useSelector(sbcFiltersSelector);
  const { user } = useSelector(getUserSelector);
  const [draft, setDraft] = useState<SolveSettings>(() =>
    pickSolveSettings(filters),
  );

  /* Every change goes through here: the draft always moves, and in live
     mode the store moves with it. */
  const commit = (next: SolveSettings) => {
    setDraft(next);
    if (mode === "live") dispatch(applySolveSettings(normaliseRatings(next)));
  };
  const set = <K extends keyof SolveSettings>(
    key: K,
    value: SolveSettings[K],
  ) => commit({ ...draft, [key]: value });

  const setBool = (key: SolveSettingsBoolKey, value: boolean) =>
    set(key, value);

  const addTo = (lane: LaneKind, player: SearchedPlayer) =>
    commit(addToLane(draft, lane, player));
  const removeFrom = (lane: LaneKind, resourceId: number) =>
    commit(removeFromLane(draft, lane, resourceId));

  /* Forcing players in or out needs an account. The ask comes when the
     search is focused, and the login dialog takes the panel's place. */
  const promptLogin = () => {
    if (user?.uuid) return false;
    dispatch(
      showModal({
        modalType: "login-required",
        freeFeatureLoggedInRequiredHook: "player-inclusion",
      }),
    );
    onDismiss?.();
    return true;
  };

  const apply = () => {
    dispatch(applySolveSettings(normaliseRatings(draft)));
    onApply?.();
  };

  const twoUp = compact ? "grid-cols-1" : "grid-cols-2";
  const primaryLabel = applyLabel ?? t("common:apply");

  return (
    <div
      className={cn(
        "flex min-h-0 flex-col text-neutral-100",
        !compact && "max-h-[80vh]",
      )}
    >
      {compact && (
        <SheetHeader
          title={t("customize.title")}
          onClose={onClose ?? apply}
          closeLabel={t("customize.closeSheet")}
          action={
            <Button variant="primary" size="small" onClick={apply}>
              {primaryLabel}
            </Button>
          }
        />
      )}
      <ScrollArea className={cn(!compact && "min-h-0")}>
        <div className={cn(!compact && "max-h-[calc(80vh-4rem)]")}>
          <section className="flex flex-col gap-4 p-4">
            <p className="max-w-[62ch] text-sm text-foreground-muted">
              <Trans
                ns="sbcs"
                i18nKey="customize.intro"
                components={{
                  b: <span className="font-semibold text-neutral-200" />,
                }}
              />
            </p>

            <SectionLabel>{t("common:filters")}</SectionLabel>

            <div className={cn("grid gap-4", twoUp)}>
              <Well className="flex h-12 items-center gap-3 px-3">
                <span className="text-sm font-semibold">
                  {t("common:rating")}
                </span>
                <div className="ml-auto">
                  <RangeFilter
                    minValue={draft.minRating}
                    maxValue={draft.maxRating}
                    onChangeMin={(v) => set("minRating", v ?? null)}
                    onChangeMax={(v) => set("maxRating", v ?? null)}
                    minPlaceHolder={RATING_MIN}
                    maxPlaceHolder={RATING_MAX}
                    slim
                    newDesignSystem
                  />
                </div>
              </Well>
              <Well>
                <SettingRow
                  label={t("customize.onlyUntradeables")}
                  onClick={() =>
                    setBool("useUntradeablesOnly", !draft.useUntradeablesOnly)
                  }
                  control={
                    <Switch
                      isActive={draft.useUntradeablesOnly}
                      onChange={(on) => setBool("useUntradeablesOnly", on)}
                    />
                  }
                />
              </Well>
            </div>

            <div className={cn("grid gap-4", twoUp)}>
              <Well className="divide-y divide-neutral-750">
                {PRIORITISE_KEYS.map((key) => (
                  <CheckRow
                    key={key}
                    label={t(LABELS[key])}
                    checked={draft[key]}
                    onChange={() => setBool(key, !draft[key])}
                  />
                ))}
              </Well>
              <Well className="divide-y divide-neutral-750">
                {INCLUDE_EXCLUDE_KEYS.map((key) => (
                  <SettingRow
                    key={key}
                    label={t(LABELS[key])}
                    control={
                      <IncludeExcludeToggle
                        variant="pill"
                        value={draft[key] ? "exclude" : "include"}
                        onChange={(value) => setBool(key, value === "exclude")}
                      />
                    }
                  />
                ))}
              </Well>
            </div>
          </section>

          <section className="min-w-0">
            <div className="flex flex-col gap-1 px-4 py-4">
              <SectionLabel>{t("customize.specificPlayers")}</SectionLabel>
              <span className="text-xs font-medium text-foreground-muted">
                {t("customize.specificPlayersHint")}
              </span>
            </div>
            <PlayerSearch
              included={draft.includedPlayers}
              excluded={draft.excludedPlayers}
              onAdd={addTo}
              onRemove={removeFrom}
              onFocusGate={promptLogin}
            />
          </section>
        </div>
      </ScrollArea>

      {!compact && (
        <div className="flex shrink-0 justify-end border-t border-neutral-700 bg-neutral-900 p-4">
          <Button variant="primary" size="small" onClick={apply}>
            {primaryLabel}
          </Button>
        </div>
      )}
    </div>
  );
};
